import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, X, CheckCircle, Eye, DollarSign, Calculator, FileText, Wallet, CreditCard, Users, BarChart3, Download, Calendar, Edit, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Payroll = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [activeTab, setActiveTab] = useState('payroll');
  const [expandedPayroll, setExpandedPayroll] = useState(null);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedLoan, setExpandedLoan] = useState(null);
  const [editingLoan, setEditingLoan] = useState(null);
  const [showEditLoanModal, setShowEditLoanModal] = useState(false);
  
  // Monthly Report State
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    project_id: '',  // Optional — auto-allocated by cost-allocation engine if empty
    period_start: '',
    period_end: '',
    base_salary: 0,
    housing_allowance: 0,
    transport_allowance: 0,
    food_allowance: 0,
    other_allowances: 0,
    overtime_hours: 0,
    overtime_rate: 1.5,
    overtime_amount: 0,
    bonuses: 0,
    bonus_reason: '',
    absence_days: 0,
    absence_deduction: 0,
    late_deduction: 0,
    other_deductions: 0,
    deduction_reason: '',
    loan_deduction: 0,
    loan_balance: 0,
    gosi_employee: 0,
    gosi_company: 0,
    total_earnings: 0,
    total_deductions: 0,
    net_salary: 0,
    notes: ''
  });

  const [loanFormData, setLoanFormData] = useState({
    employee_id: '',
    loan_type: 'advance',
    amount: 0,
    monthly_deduction: 0,
    reason: '',
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'report') {
      fetchMonthlyReport();
    }
  }, [activeTab, reportMonth]);

  const fetchData = async () => {
    try {
      const [payrollRes, employeesRes, loansRes, projectsRes] = await Promise.all([
        axiosInstance.get('/payroll'),
        axiosInstance.get('/employees'),
        axiosInstance.get('/employee-loans'),
        axiosInstance.get('/projects').catch(() => ({ data: [] }))
      ]);
      setPayrolls(payrollRes.data.filter(p => p.approval_status === 'approved'));
      setEmployees(employeesRes.data);
      setLoans(loansRes.data.filter(l => l.approval_status === 'approved'));
      setProjects(projectsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyReport = async () => {
    setLoadingReport(true);
    try {
      const response = await axiosInstance.get(`/payroll/report/monthly?month=${reportMonth}`);
      setMonthlyReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch monthly report');
    } finally {
      setLoadingReport(false);
    }
  };

  const deletePayroll = async (payrollId) => {
    if (!window.confirm('Are you sure you want to delete this payroll record?')) return;
    try {
      await axiosInstance.delete(`/payroll/${payrollId}`);
      toast.success('Payroll deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payroll');
    }
  };

  const openEditModal = (payroll) => {
    setEditingPayroll({
      ...payroll,
      period_start: payroll.period_start,
      period_end: payroll.period_end
    });
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditingPayroll(prev => {
      const updated = { ...prev, [field]: value };
      
      // Recalculate totals
      const totalEarnings = (parseFloat(updated.base_salary) || 0) + 
        (parseFloat(updated.housing_allowance) || 0) + 
        (parseFloat(updated.transport_allowance) || 0) + 
        (parseFloat(updated.food_allowance) || 0) + 
        (parseFloat(updated.other_allowances) || 0) + 
        (parseFloat(updated.overtime_amount) || 0) + 
        (parseFloat(updated.bonuses) || 0);
      
      const totalDeductions = (parseFloat(updated.absence_deduction) || 0) + 
        (parseFloat(updated.late_deduction) || 0) + 
        (parseFloat(updated.other_deductions) || 0) + 
        (parseFloat(updated.loan_deduction) || 0) + 
        (parseFloat(updated.gosi_employee) || 0);
      
      updated.total_earnings = totalEarnings;
      updated.total_deductions = totalDeductions;
      updated.net_salary = totalEarnings - totalDeductions;
      
      return updated;
    });
  };

  const savePayrollEdit = async () => {
    try {
      await axiosInstance.put(`/payroll/${editingPayroll.id}`, editingPayroll);
      toast.success('Payroll updated successfully');
      setShowEditModal(false);
      setEditingPayroll(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payroll');
    }
  };

  // Loan functions
  const deleteLoan = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this loan?')) return;
    try {
      await axiosInstance.delete(`/employee-loans/${loanId}`);
      toast.success('Loan deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete loan');
    }
  };

  const openEditLoanModal = (loan) => {
    setEditingLoan({...loan});
    setShowEditLoanModal(true);
  };

  const handleEditLoanChange = (field, value) => {
    setEditingLoan(prev => ({...prev, [field]: value}));
  };

  const saveLoanEdit = async () => {
    try {
      await axiosInstance.put(`/employee-loans/${editingLoan.id}`, editingLoan);
      toast.success('Loan updated successfully');
      setShowEditLoanModal(false);
      setEditingLoan(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update loan');
    }
  };

  const calculateOvertimeAmount = (baseSalary, hours, rate) => {
    const hourlyRate = baseSalary / 30 / 8;
    return hourlyRate * hours * rate;
  };

  const calculateAbsenceDeduction = (baseSalary, days) => {
    const dailyRate = baseSalary / 30;
    return dailyRate * days;
  };

  const calculateGOSI = async (baseSalary) => {
    try {
      const response = await axiosInstance.get(`/payroll/calculate-gosi/${baseSalary}`);
      return response.data;
    } catch {
      return {
        gosi_employee: baseSalary * 0.0975,
        gosi_company: baseSalary * 0.1175
      };
    }
  };

  const getEmployeeLoans = async (employeeId) => {
    try {
      const response = await axiosInstance.get(`/employee-loans/employee/${employeeId}`);
      return response.data.filter(l => l.status === 'active');
    } catch {
      return [];
    }
  };

  const recalculateTotals = (data) => {
    const totalEarnings = 
      parseFloat(data.base_salary || 0) +
      parseFloat(data.housing_allowance || 0) +
      parseFloat(data.transport_allowance || 0) +
      parseFloat(data.food_allowance || 0) +
      parseFloat(data.other_allowances || 0) +
      parseFloat(data.overtime_amount || 0) +
      parseFloat(data.bonuses || 0);

    const totalDeductions = 
      parseFloat(data.absence_deduction || 0) +
      parseFloat(data.late_deduction || 0) +
      parseFloat(data.other_deductions || 0) +
      parseFloat(data.loan_deduction || 0) +
      parseFloat(data.gosi_employee || 0);

    const netSalary = totalEarnings - totalDeductions;

    return {
      ...data,
      total_earnings: totalEarnings,
      total_deductions: totalDeductions,
      net_salary: netSalary
    };
  };

  const handleEmployeeChange = async (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      const gosi = await calculateGOSI(employee.salary);
      const employeeLoans = await getEmployeeLoans(employeeId);
      const totalLoanDeduction = employeeLoans.reduce((sum, l) => sum + l.monthly_deduction, 0);
      const totalLoanBalance = employeeLoans.reduce((sum, l) => sum + l.remaining_balance, 0);

      const newData = {
        ...formData,
        employee_id: employeeId,
        base_salary: employee.salary,
        gosi_employee: gosi.gosi_employee,
        gosi_company: gosi.gosi_company,
        loan_deduction: totalLoanDeduction,
        loan_balance: totalLoanBalance
      };

      setFormData(recalculateTotals(newData));
    }
  };

  const handleFieldChange = async (field, value) => {
    let newData = { ...formData, [field]: value };

    if (['overtime_hours', 'overtime_rate'].includes(field) || field === 'base_salary') {
      newData.overtime_amount = calculateOvertimeAmount(
        newData.base_salary,
        newData.overtime_hours,
        newData.overtime_rate
      );
    }

    if (field === 'absence_days' || field === 'base_salary') {
      newData.absence_deduction = calculateAbsenceDeduction(
        newData.base_salary,
        newData.absence_days
      );
    }

    if (field === 'base_salary') {
      const gosi = await calculateGOSI(value);
      newData.gosi_employee = gosi.gosi_employee;
      newData.gosi_company = gosi.gosi_company;
    }

    setFormData(recalculateTotals(newData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/payroll', formData);
      toast.success('Payroll created successfully', {
        description: 'Waiting for admin approval'
      });
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payroll');
    }
  };

  const handleLoanSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/employee-loans', loanFormData);
      toast.success('Loan/Advance created successfully', {
        description: 'Waiting for admin approval'
      });
      setShowLoanModal(false);
      resetLoanForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create loan');
    }
  };

  const handleStatusUpdate = async (id, status, paidDate = null) => {
    try {
      const params = new URLSearchParams({ status });
      if (paidDate) params.append('paid_date', paidDate);
      
      await axiosInstance.put(`/payroll/${id}/status?${params.toString()}`);
      toast.success('Payroll status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const viewPayrollDetails = (payroll) => {
    setSelectedPayroll(payroll);
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      period_start: '',
      period_end: '',
      base_salary: 0,
      housing_allowance: 0,
      transport_allowance: 0,
      food_allowance: 0,
      other_allowances: 0,
      overtime_hours: 0,
      overtime_rate: 1.5,
      overtime_amount: 0,
      bonuses: 0,
      bonus_reason: '',
      absence_days: 0,
      absence_deduction: 0,
      late_deduction: 0,
      other_deductions: 0,
      deduction_reason: '',
      loan_deduction: 0,
      loan_balance: 0,
      gosi_employee: 0,
      gosi_company: 0,
      total_earnings: 0,
      total_deductions: 0,
      net_salary: 0,
      notes: ''
    });
  };

  const resetLoanForm = () => {
    setLoanFormData({
      employee_id: '',
      loan_type: 'advance',
      amount: 0,
      monthly_deduction: 0,
      reason: '',
      start_date: new Date().toISOString().split('T')[0]
    });
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Export Monthly Report to PDF
  const exportReportToPDF = () => {
    if (!monthlyReport) {
      toast.error('Please generate a report first');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(102, 51, 153);
      doc.text('Monthly Payroll Report', pageWidth / 2, 20, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setTextColor(100);
      doc.text(getMonthName(monthlyReport.month), pageWidth / 2, 30, { align: 'center' });
      
      // Summary Box
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setDrawColor(200);
      doc.setFillColor(245, 245, 245);
      doc.roundedRect(14, 40, pageWidth - 28, 35, 3, 3, 'F');
      
      doc.setFontSize(10);
      doc.text('Summary', 20, 50);
      doc.setFontSize(9);
      
      const summaryY = 58;
      doc.text(`Employees: ${monthlyReport.employee_count}`, 20, summaryY);
      doc.text(`Total Earnings: ${monthlyReport.summary.total_earnings?.toLocaleString()} SAR`, 70, summaryY);
      doc.text(`Total Deductions: ${monthlyReport.summary.deductions.total?.toLocaleString()} SAR`, 130, summaryY);
      
      doc.setFontSize(11);
      doc.setTextColor(102, 51, 153);
      doc.text(`Net Payroll: ${monthlyReport.summary.total_net_salary?.toLocaleString()} SAR`, 20, 70);
      
      // Earnings Breakdown Table
      doc.setFontSize(11);
      doc.setTextColor(34, 139, 34);
      doc.text('Earnings Breakdown', 14, 85);
      
      autoTable(doc, {
        startY: 90,
        head: [['Category', 'Amount (SAR)']],
        body: [
          ['Base Salaries', monthlyReport.summary.total_base_salary?.toLocaleString() || '0'],
          ['Housing Allowance', monthlyReport.summary.allowances.housing?.toLocaleString() || '0'],
          ['Transport Allowance', monthlyReport.summary.allowances.transport?.toLocaleString() || '0'],
          ['Food Allowance', monthlyReport.summary.allowances.food?.toLocaleString() || '0'],
          ['Other Allowances', monthlyReport.summary.allowances.other?.toLocaleString() || '0'],
          ['Overtime', monthlyReport.summary.total_overtime?.toLocaleString() || '0'],
          ['Bonuses', monthlyReport.summary.total_bonuses?.toLocaleString() || '0'],
          ['Total Earnings', monthlyReport.summary.total_earnings?.toLocaleString() || '0']
        ],
        headStyles: { fillColor: [34, 139, 34], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 255, 240] },
        columnStyles: { 1: { halign: 'right' } },
        margin: { left: 14 },
        tableWidth: 85
      });
      
      // Deductions Breakdown Table
      doc.setFontSize(11);
      doc.setTextColor(220, 20, 60);
      doc.text('Deductions Breakdown', 110, 85);
      
      autoTable(doc, {
        startY: 90,
        head: [['Category', 'Amount (SAR)']],
        body: [
          ['Absence Deductions', monthlyReport.summary.deductions.absence?.toLocaleString() || '0'],
          ['Late Deductions', monthlyReport.summary.deductions.late?.toLocaleString() || '0'],
          ['Loan Deductions', monthlyReport.summary.deductions.loans?.toLocaleString() || '0'],
          ['GOSI (Employee)', monthlyReport.summary.deductions.gosi_employee?.toLocaleString() || '0'],
          ['Other Deductions', monthlyReport.summary.deductions.other?.toLocaleString() || '0'],
          ['Total Deductions', monthlyReport.summary.deductions.total?.toLocaleString() || '0'],
          ['GOSI (Company)', monthlyReport.summary.gosi_company?.toLocaleString() || '0']
        ],
        headStyles: { fillColor: [220, 20, 60], textColor: 255 },
        alternateRowStyles: { fillColor: [255, 240, 240] },
        columnStyles: { 1: { halign: 'right' } },
        margin: { left: 110 },
        tableWidth: 85
      });
      
      // Employee Breakdown Table
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text('Employee Breakdown', 14, 180);
      
      const employeeData = monthlyReport.employees.map(emp => [
        emp.payroll_number || '-',
        emp.employee_name || '-',
        emp.employee_position || '-',
        (emp.base_salary || 0).toLocaleString(),
        (emp.total_allowances || 0).toLocaleString(),
        (emp.total_deductions || 0).toLocaleString(),
        (emp.net_salary || 0).toLocaleString(),
        emp.status || '-'
      ]);
      
      autoTable(doc, {
        startY: 185,
        head: [['Payroll #', 'Employee', 'Position', 'Base', 'Allowances', 'Deductions', 'Net Salary', 'Status']],
        body: employeeData.length > 0 ? employeeData : [['No records', '', '', '', '', '', '', '']],
        headStyles: { fillColor: [102, 51, 153], textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        },
        margin: { left: 14, right: 14 }
      });
      
      // Footer
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 150;
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, finalY);
      doc.text(`Avg Salary: ${monthlyReport.summary.average_salary?.toLocaleString()} SAR`, pageWidth - 14, finalY, { align: 'right' });
      
      // Download PDF directly
      doc.save(`Payroll_Report_${monthlyReport.month}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF Export Error:', error);
      toast.error('Failed to export report');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading payroll...</div>;
  }

  return (
    <div data-testid="payroll-page">
      <div className="page-header">
        <h2 className="page-title">Payroll Management</h2>
        <div className="flex gap-3">
          <button
            data-testid="add-loan-button"
            onClick={() => setShowLoanModal(true)}
            className="btn-secondary"
          >
            <CreditCard className="w-5 h-5 inline mr-2" />
            New Loan/Advance
          </button>
          <button
            data-testid="add-payroll-button"
            onClick={() => setShowModal(true)}
            className="btn-primary"
          >
            <Plus className="w-5 h-5 inline mr-2" />
            New Payroll
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <button
          data-testid="tab-payroll"
          onClick={() => setActiveTab('payroll')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all ${
            activeTab === 'payroll'
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          <Wallet className="w-5 h-5 inline mr-2" />
          Payroll Records ({payrolls.length})
        </button>
        <button
          data-testid="tab-loans"
          onClick={() => setActiveTab('loans')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all ${
            activeTab === 'loans'
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          <CreditCard className="w-5 h-5 inline mr-2" />
          Loans & Advances ({loans.length})
        </button>
        <button
          data-testid="tab-report"
          onClick={() => setActiveTab('report')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all ${
            activeTab === 'report'
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          <BarChart3 className="w-5 h-5 inline mr-2" />
          Monthly Report
        </button>
      </div>

      {/* Payroll Tab */}
      {activeTab === 'payroll' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th></th>
                <th>Payroll #</th>
                <th>Date</th>
                <th>Employee</th>
                <th>Department</th>
                <th>Period</th>
                <th>Base Salary</th>
                <th>Earnings</th>
                <th>Deductions</th>
                <th>Net Salary</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payrolls.length > 0 ? (
                payrolls.map((payroll, idx) => (
                  <React.Fragment key={payroll.id}>
                    <tr 
                      data-testid={`payroll-row-${payroll.id}`}
                      className={`cursor-pointer hover:bg-blue-50 ${expandedPayroll === idx ? 'bg-blue-50' : ''}`}
                      onClick={() => setExpandedPayroll(expandedPayroll === idx ? null : idx)}
                    >
                      <td className="w-8">
                        {expandedPayroll === idx ? 
                          <ChevronUp className="w-4 h-4 text-gray-500" /> : 
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        }
                      </td>
                      <td className="font-mono text-purple-600">{payroll.payroll_number}</td>
                      <td className="text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(payroll.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="font-semibold">{payroll.employee_name}</td>
                      <td>{payroll.employee_department || '-'}</td>
                      <td className="text-sm">
                        {new Date(payroll.period_start).toLocaleDateString()} - {new Date(payroll.period_end).toLocaleDateString()}
                      </td>
                      <td>{payroll.base_salary?.toLocaleString()} SAR</td>
                      <td className="text-green-600 font-semibold">+{payroll.total_earnings?.toLocaleString()} SAR</td>
                      <td className="text-red-600 font-semibold">-{payroll.total_deductions?.toLocaleString()} SAR</td>
                      <td className="font-bold text-lg">{payroll.net_salary?.toLocaleString()} SAR</td>
                      <td>
                        <span className={`status-badge ${payroll.status}`}>
                          {payroll.status}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <button
                            data-testid={`view-payroll-${payroll.id}`}
                            onClick={() => viewPayrollDetails(payroll)}
                            className="btn-icon"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => openEditModal(payroll)}
                            className="btn-icon"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-green-600" />
                          </button>
                          <button
                            onClick={() => deletePayroll(payroll.id)}
                            className="btn-icon"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                          {payroll.status === 'pending' && (
                            <button
                              data-testid={`mark-paid-${payroll.id}`}
                              onClick={() => handleStatusUpdate(payroll.id, 'completed', new Date().toISOString().split('T')[0])}
                              className="btn-icon"
                              title="Mark as Paid"
                            >
                              <CheckCircle className="w-4 h-4 text-purple-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedPayroll === idx && (
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                        <td colSpan="12" className="p-4">
                          <div className="grid grid-cols-6 gap-3">
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Base Salary</p>
                              <p className="font-bold text-lg">{payroll.base_salary?.toLocaleString()} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Housing Allowance</p>
                              <p className="font-bold text-lg text-green-600">+{payroll.housing_allowance?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Transport Allowance</p>
                              <p className="font-bold text-lg text-green-600">+{payroll.transport_allowance?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Food Allowance</p>
                              <p className="font-bold text-lg text-green-600">+{payroll.food_allowance?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Overtime</p>
                              <p className="font-bold text-lg text-blue-600">+{payroll.overtime_amount?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Bonuses</p>
                              <p className="font-bold text-lg text-purple-600">+{payroll.bonuses?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-red-400">
                              <p className="text-xs text-gray-500">Absence Deduction</p>
                              <p className="font-bold text-lg text-red-600">-{payroll.absence_deduction?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-red-400">
                              <p className="text-xs text-gray-500">Late Deduction</p>
                              <p className="font-bold text-lg text-red-600">-{payroll.late_deduction?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-red-400">
                              <p className="text-xs text-gray-500">Loan Deduction</p>
                              <p className="font-bold text-lg text-red-600">-{payroll.loan_deduction?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-red-400">
                              <p className="text-xs text-gray-500">GOSI (Employee)</p>
                              <p className="font-bold text-lg text-red-600">-{payroll.gosi_employee?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-red-400">
                              <p className="text-xs text-gray-500">Other Deductions</p>
                              <p className="font-bold text-lg text-red-600">-{payroll.other_deductions?.toLocaleString() || 0} SAR</p>
                            </div>
                            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-3 rounded-lg shadow-sm text-white">
                              <p className="text-xs opacity-80">Net Salary</p>
                              <p className="font-bold text-xl">{payroll.net_salary?.toLocaleString()} SAR</p>
                            </div>
                          </div>
                          {payroll.notes && (
                            <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                              <span className="font-semibold text-yellow-700">Notes:</span> {payroll.notes}
                            </div>
                          )}
                          {payroll.bonus_reason && (
                            <div className="mt-2 p-3 bg-green-50 rounded-lg">
                              <span className="font-semibold text-green-700">Bonus Reason:</span> {payroll.bonus_reason}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-12 text-gray-500">
                    No approved payroll records found. Create a new payroll and wait for approval.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Loans Tab */}
      {activeTab === 'loans' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th></th>
                <th>Loan #</th>
                <th>Date</th>
                <th>Employee</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Monthly Deduction</th>
                <th>Remaining Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loans.length > 0 ? (
                loans.map((loan, idx) => (
                  <React.Fragment key={loan.id}>
                    <tr 
                      data-testid={`loan-row-${loan.id}`}
                      className={`cursor-pointer hover:bg-purple-50 ${expandedLoan === idx ? 'bg-purple-50' : ''}`}
                      onClick={() => setExpandedLoan(expandedLoan === idx ? null : idx)}
                    >
                      <td className="w-8">
                        {expandedLoan === idx ? 
                          <ChevronUp className="w-4 h-4 text-gray-500" /> : 
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        }
                      </td>
                      <td className="font-mono text-purple-600">{loan.loan_number}</td>
                      <td className="text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {loan.start_date || new Date(loan.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="font-semibold">{loan.employee_name}</td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          loan.loan_type === 'loan' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {loan.loan_type === 'loan' ? 'Loan' : 'Advance'}
                        </span>
                      </td>
                      <td>{loan.amount?.toLocaleString()} SAR</td>
                      <td>{loan.monthly_deduction?.toLocaleString()} SAR</td>
                      <td className="font-semibold">{loan.remaining_balance?.toLocaleString()} SAR</td>
                      <td>
                        <span className={`status-badge ${loan.status}`}>
                          {loan.status}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openEditLoanModal(loan)}
                            className="btn-icon"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-green-600" />
                          </button>
                          <button
                            onClick={() => deleteLoan(loan.id)}
                            className="btn-icon"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedLoan === idx && (
                      <tr className="bg-gradient-to-r from-purple-50 to-indigo-50">
                        <td colSpan="10" className="p-4">
                          <div className="grid grid-cols-5 gap-3">
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Loan Amount</p>
                              <p className="font-bold text-lg text-blue-600">{loan.amount?.toLocaleString()} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Monthly Deduction</p>
                              <p className="font-bold text-lg text-orange-600">{loan.monthly_deduction?.toLocaleString()} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Total Paid</p>
                              <p className="font-bold text-lg text-green-600">{((loan.amount || 0) - (loan.remaining_balance || 0)).toLocaleString()} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Remaining Balance</p>
                              <p className="font-bold text-lg text-red-600">{loan.remaining_balance?.toLocaleString()} SAR</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Progress</p>
                              <div className="w-full bg-gray-200 rounded-full h-3 mt-1">
                                <div 
                                  className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full" 
                                  style={{width: `${loan.amount ? ((loan.amount - (loan.remaining_balance || 0)) / loan.amount * 100) : 0}%`}}
                                ></div>
                              </div>
                              <p className="text-sm mt-1 font-semibold">{loan.amount ? Math.round((loan.amount - (loan.remaining_balance || 0)) / loan.amount * 100) : 0}% paid</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Start Date</p>
                              <p className="font-bold">{loan.start_date || '-'}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">End Date</p>
                              <p className="font-bold">{loan.end_date || '-'}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm">
                              <p className="text-xs text-gray-500">Duration</p>
                              <p className="font-bold">{loan.duration_months ? `${loan.duration_months} months` : '-'}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm col-span-2">
                              <p className="text-xs text-gray-500">Reason</p>
                              <p className="font-semibold">{loan.reason || 'No reason provided'}</p>
                            </div>
                          </div>
                          {loan.notes && (
                            <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                              <span className="font-semibold text-yellow-700">Notes:</span> {loan.notes}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-12 text-gray-500">
                    No approved loans or advances found. Create a new loan and wait for approval.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Monthly Report Tab */}
      {activeTab === 'report' && (
        <div className="space-y-6">
          {/* Month Selector */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center gap-4">
              <Calendar className="w-5 h-5 text-purple-600" />
              <label className="font-semibold text-gray-700">Select Month:</label>
              <input
                type="month"
                value={reportMonth}
                onChange={(e) => setReportMonth(e.target.value)}
                className="form-input w-48"
                data-testid="report-month-selector"
              />
              <button
                onClick={fetchMonthlyReport}
                className="btn-primary"
              >
                Generate Report
              </button>
              {monthlyReport && (
                <button
                  onClick={exportReportToPDF}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="export-pdf-button"
                >
                  <Download className="w-4 h-4" />
                  Export PDF
                </button>
              )}
            </div>
          </div>

          {loadingReport ? (
            <div className="text-center py-12">Loading report...</div>
          ) : monthlyReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-6 h-6" />
                    <span className="text-blue-100">Employees</span>
                  </div>
                  <p className="text-3xl font-bold">{monthlyReport.employee_count}</p>
                  <p className="text-blue-100 text-sm mt-1">{getMonthName(monthlyReport.month)}</p>
                </div>
                
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-6 h-6" />
                    <span className="text-green-100">Total Earnings</span>
                  </div>
                  <p className="text-3xl font-bold">{monthlyReport.summary.total_earnings?.toLocaleString()}</p>
                  <p className="text-green-100 text-sm mt-1">SAR</p>
                </div>
                
                <div className="bg-gradient-to-br from-red-500 to-red-600 p-6 rounded-xl text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-6 h-6" />
                    <span className="text-red-100">Total Deductions</span>
                  </div>
                  <p className="text-3xl font-bold">{monthlyReport.summary.deductions.total?.toLocaleString()}</p>
                  <p className="text-red-100 text-sm mt-1">SAR</p>
                </div>
                
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <Wallet className="w-6 h-6" />
                    <span className="text-purple-100">Net Payroll</span>
                  </div>
                  <p className="text-3xl font-bold">{monthlyReport.summary.total_net_salary?.toLocaleString()}</p>
                  <p className="text-purple-100 text-sm mt-1">SAR</p>
                </div>
              </div>

              {/* Detailed Summary */}
              <div className="grid grid-cols-2 gap-6">
                {/* Earnings Breakdown */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    Earnings Breakdown
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Base Salaries</span>
                      <span className="font-semibold">{monthlyReport.summary.total_base_salary?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Housing Allowance</span>
                      <span className="font-semibold">{monthlyReport.summary.allowances.housing?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Transport Allowance</span>
                      <span className="font-semibold">{monthlyReport.summary.allowances.transport?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Food Allowance</span>
                      <span className="font-semibold">{monthlyReport.summary.allowances.food?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Other Allowances</span>
                      <span className="font-semibold">{monthlyReport.summary.allowances.other?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Overtime</span>
                      <span className="font-semibold">{monthlyReport.summary.total_overtime?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Bonuses</span>
                      <span className="font-semibold">{monthlyReport.summary.total_bonuses?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 bg-green-50 px-3 rounded-lg font-bold text-green-700">
                      <span>Total Earnings</span>
                      <span>{monthlyReport.summary.total_earnings?.toLocaleString()} SAR</span>
                    </div>
                  </div>
                </div>

                {/* Deductions Breakdown */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    Deductions Breakdown
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Absence Deductions</span>
                      <span className="font-semibold">{monthlyReport.summary.deductions.absence?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Late Deductions</span>
                      <span className="font-semibold">{monthlyReport.summary.deductions.late?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Loan Deductions</span>
                      <span className="font-semibold">{monthlyReport.summary.deductions.loans?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">GOSI (Employee Share)</span>
                      <span className="font-semibold">{monthlyReport.summary.deductions.gosi_employee?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Other Deductions</span>
                      <span className="font-semibold">{monthlyReport.summary.deductions.other?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 bg-red-50 px-3 rounded-lg font-bold text-red-700">
                      <span>Total Deductions</span>
                      <span>{monthlyReport.summary.deductions.total?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between py-2 border-t-2 mt-4 pt-4">
                      <span className="text-gray-600">GOSI (Company Share)</span>
                      <span className="font-semibold text-blue-600">{monthlyReport.summary.gosi_company?.toLocaleString()} SAR</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Employee Breakdown Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-800">Employee Breakdown</h3>
                  <span className="text-sm text-gray-500">
                    Average Net Salary: <strong className="text-purple-600">{monthlyReport.summary.average_salary?.toLocaleString()} SAR</strong>
                  </span>
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Payroll #</th>
                      <th>Employee</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Base Salary</th>
                      <th>Allowances</th>
                      <th>Overtime</th>
                      <th>Bonuses</th>
                      <th>Deductions</th>
                      <th>Net Salary</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyReport.employees.length > 0 ? (
                      monthlyReport.employees.map((emp, idx) => (
                        <tr key={idx}>
                          <td className="font-mono text-purple-600">{emp.payroll_number}</td>
                          <td className="font-semibold">{emp.employee_name}</td>
                          <td>{emp.employee_position || '-'}</td>
                          <td>{emp.employee_department || '-'}</td>
                          <td>{emp.base_salary?.toLocaleString()} SAR</td>
                          <td className="text-green-600">+{emp.total_allowances?.toLocaleString()} SAR</td>
                          <td className="text-green-600">+{emp.overtime?.toLocaleString()} SAR</td>
                          <td className="text-green-600">+{emp.bonuses?.toLocaleString()} SAR</td>
                          <td className="text-red-600">-{emp.total_deductions?.toLocaleString()} SAR</td>
                          <td className="font-bold">{emp.net_salary?.toLocaleString()} SAR</td>
                          <td>
                            <span className={`status-badge ${emp.status}`}>
                              {emp.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11" className="text-center py-12 text-gray-500">
                          No payroll records found for this month.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Select a month and click "Generate Report" to view the monthly payroll summary.
            </div>
          )}
        </div>
      )}

      {/* New Payroll Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <Calculator className="w-6 h-6 inline mr-2 text-purple-600" />
                New Payroll
              </h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto pr-2">
              {/* Employee Selection */}
              <div className="bg-gray-50 p-4 rounded-xl mb-4">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Employee Information
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Employee *</label>
                    <select
                      data-testid="payroll-employee-select"
                      value={formData.employee_id}
                      onChange={(e) => handleEmployeeChange(e.target.value)}
                      className="form-select"
                      required
                    >
                      <option value="">Select Employee</option>
                      {employees.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.full_name} - {employee.position}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project (optional)</label>
                    <select
                      data-testid="payroll-project-select"
                      value={formData.project_id}
                      onChange={(e) => handleFieldChange('project_id', e.target.value)}
                      className="form-select"
                    >
                      <option value="">— Auto (rule engine) —</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Period Start *</label>
                    <input
                      data-testid="payroll-start-date-input"
                      type="date"
                      value={formData.period_start}
                      onChange={(e) => handleFieldChange('period_start', e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Period End *</label>
                    <input
                      data-testid="payroll-end-date-input"
                      type="date"
                      value={formData.period_end}
                      onChange={(e) => handleFieldChange('period_end', e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Earnings Section */}
              <div className="bg-green-50 p-4 rounded-xl mb-4">
                <h4 className="font-semibold text-green-700 mb-3 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Earnings
                </h4>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="form-group">
                    <label className="form-label">Base Salary</label>
                    <input
                      data-testid="payroll-base-salary-input"
                      type="number"
                      value={formData.base_salary}
                      className="form-input bg-gray-100"
                      readOnly
                    />
                  </div>
                </div>

                <p className="text-sm font-medium text-green-600 mb-2">Allowances</p>
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="form-group">
                    <label className="form-label text-sm">Housing</label>
                    <input
                      data-testid="housing-allowance-input"
                      type="number"
                      value={formData.housing_allowance}
                      onChange={(e) => handleFieldChange('housing_allowance', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Transport</label>
                    <input
                      data-testid="transport-allowance-input"
                      type="number"
                      value={formData.transport_allowance}
                      onChange={(e) => handleFieldChange('transport_allowance', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Food</label>
                    <input
                      data-testid="food-allowance-input"
                      type="number"
                      value={formData.food_allowance}
                      onChange={(e) => handleFieldChange('food_allowance', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Other</label>
                    <input
                      data-testid="other-allowances-input"
                      type="number"
                      value={formData.other_allowances}
                      onChange={(e) => handleFieldChange('other_allowances', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                </div>

                <p className="text-sm font-medium text-green-600 mb-2">Overtime</p>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="form-group">
                    <label className="form-label text-sm">Hours</label>
                    <input
                      data-testid="overtime-hours-input"
                      type="number"
                      value={formData.overtime_hours}
                      onChange={(e) => handleFieldChange('overtime_hours', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Rate</label>
                    <select
                      data-testid="overtime-rate-select"
                      value={formData.overtime_rate}
                      onChange={(e) => handleFieldChange('overtime_rate', parseFloat(e.target.value))}
                      className="form-select"
                    >
                      <option value="1.5">1.5x (Regular)</option>
                      <option value="2">2x (Weekend/Holiday)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Amount</label>
                    <input
                      type="number"
                      value={formData.overtime_amount.toFixed(2)}
                      className="form-input bg-gray-100"
                      readOnly
                    />
                  </div>
                </div>

                <p className="text-sm font-medium text-green-600 mb-2">Bonuses</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label text-sm">Amount</label>
                    <input
                      data-testid="bonuses-input"
                      type="number"
                      value={formData.bonuses}
                      onChange={(e) => handleFieldChange('bonuses', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Reason</label>
                    <input
                      type="text"
                      value={formData.bonus_reason}
                      onChange={(e) => handleFieldChange('bonus_reason', e.target.value)}
                      className="form-input"
                      placeholder="Performance bonus..."
                    />
                  </div>
                </div>
              </div>

              {/* Deductions Section */}
              <div className="bg-red-50 p-4 rounded-xl mb-4">
                <h4 className="font-semibold text-red-700 mb-3 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Deductions
                </h4>

                <p className="text-sm font-medium text-red-600 mb-2">Attendance</p>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="form-group">
                    <label className="form-label text-sm">Absence Days</label>
                    <input
                      data-testid="absence-days-input"
                      type="number"
                      value={formData.absence_days}
                      onChange={(e) => handleFieldChange('absence_days', parseInt(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Absence Deduction</label>
                    <input
                      type="number"
                      value={formData.absence_deduction.toFixed(2)}
                      className="form-input bg-gray-100"
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Late Deduction</label>
                    <input
                      data-testid="late-deduction-input"
                      type="number"
                      value={formData.late_deduction}
                      onChange={(e) => handleFieldChange('late_deduction', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                </div>

                <p className="text-sm font-medium text-red-600 mb-2">Other Deductions</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="form-group">
                    <label className="form-label text-sm">Amount</label>
                    <input
                      data-testid="other-deductions-input"
                      type="number"
                      value={formData.other_deductions}
                      onChange={(e) => handleFieldChange('other_deductions', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Reason</label>
                    <input
                      type="text"
                      value={formData.deduction_reason}
                      onChange={(e) => handleFieldChange('deduction_reason', e.target.value)}
                      className="form-input"
                      placeholder="Disciplinary action..."
                    />
                  </div>
                </div>

                <p className="text-sm font-medium text-red-600 mb-2">Loans & Advances</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="form-group">
                    <label className="form-label text-sm">Monthly Deduction</label>
                    <input
                      type="number"
                      value={formData.loan_deduction}
                      className="form-input bg-gray-100"
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Remaining Balance</label>
                    <input
                      type="number"
                      value={formData.loan_balance}
                      className="form-input bg-gray-100"
                      readOnly
                    />
                  </div>
                </div>

                <p className="text-sm font-medium text-red-600 mb-2">Social Insurance - GOSI</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label text-sm">Employee Share (9.75%)</label>
                    <input
                      type="number"
                      value={formData.gosi_employee.toFixed(2)}
                      className="form-input bg-gray-100"
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Company Share (11.75%)</label>
                    <input
                      type="number"
                      value={formData.gosi_company.toFixed(2)}
                      className="form-input bg-blue-50"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">Company contribution (not deducted from salary)</p>
                  </div>
                </div>
              </div>

              {/* Summary Section */}
              <div className="bg-gradient-to-r from-purple-100 to-indigo-100 p-4 rounded-xl mb-4">
                <h4 className="font-semibold text-purple-700 mb-3">Salary Summary</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Total Earnings</p>
                    <p className="text-xl font-bold text-green-600">+{formData.total_earnings.toFixed(2)} SAR</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Total Deductions</p>
                    <p className="text-xl font-bold text-red-600">-{formData.total_deductions.toFixed(2)} SAR</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Net Salary</p>
                    <p data-testid="payroll-net-salary" className="text-2xl font-bold text-purple-600">
                      {formData.net_salary.toFixed(2)} SAR
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  className="form-input"
                  rows="2"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-payroll-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Create Payroll
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Loan Modal */}
      {showLoanModal && (
        <div className="modal-overlay" onClick={() => setShowLoanModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <CreditCard className="w-6 h-6 inline mr-2 text-purple-600" />
                New Loan / Advance
              </h3>
              <button
                onClick={() => setShowLoanModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleLoanSubmit}>
              <div className="form-group">
                <label className="form-label">Employee *</label>
                <select
                  data-testid="loan-employee-select"
                  value={loanFormData.employee_id}
                  onChange={(e) => setLoanFormData({ ...loanFormData, employee_id: e.target.value })}
                  className="form-select"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.full_name} - {employee.position}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Type *</label>
                <select
                  data-testid="loan-type-select"
                  value={loanFormData.loan_type}
                  onChange={(e) => setLoanFormData({ ...loanFormData, loan_type: e.target.value })}
                  className="form-select"
                  required
                >
                  <option value="advance">Advance</option>
                  <option value="loan">Loan</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Amount (SAR) *</label>
                  <input
                    data-testid="loan-amount-input"
                    type="number"
                    value={loanFormData.amount}
                    onChange={(e) => setLoanFormData({ ...loanFormData, amount: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    required
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Monthly Deduction (SAR) *</label>
                  <input
                    data-testid="loan-monthly-deduction-input"
                    type="number"
                    value={loanFormData.monthly_deduction}
                    onChange={(e) => setLoanFormData({ ...loanFormData, monthly_deduction: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    required
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Start Date *</label>
                <input
                  data-testid="loan-start-date-input"
                  type="date"
                  value={loanFormData.start_date}
                  onChange={(e) => setLoanFormData({ ...loanFormData, start_date: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Reason</label>
                <textarea
                  value={loanFormData.reason}
                  onChange={(e) => setLoanFormData({ ...loanFormData, reason: e.target.value })}
                  className="form-input"
                  rows="2"
                  placeholder="Reason for loan/advance..."
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-loan-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Create Loan/Advance
                </button>
                <button
                  type="button"
                  onClick={() => setShowLoanModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payroll Detail Modal */}
      {showDetailModal && selectedPayroll && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                Payroll Details - {selectedPayroll.payroll_number}
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Employee Info */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <h4 className="font-semibold text-gray-700 mb-2">Employee Information</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Name</p>
                    <p className="font-semibold">{selectedPayroll.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Position</p>
                    <p className="font-semibold">{selectedPayroll.employee_position || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Department</p>
                    <p className="font-semibold">{selectedPayroll.employee_department || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Earnings */}
              <div className="bg-green-50 p-4 rounded-xl">
                <h4 className="font-semibold text-green-700 mb-2">Earnings</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base Salary:</span>
                    <span className="font-semibold">{selectedPayroll.base_salary?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Housing Allowance:</span>
                    <span className="font-semibold">{selectedPayroll.housing_allowance?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Transport Allowance:</span>
                    <span className="font-semibold">{selectedPayroll.transport_allowance?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Food Allowance:</span>
                    <span className="font-semibold">{selectedPayroll.food_allowance?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Other Allowances:</span>
                    <span className="font-semibold">{selectedPayroll.other_allowances?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Overtime ({selectedPayroll.overtime_hours}h x {selectedPayroll.overtime_rate}):</span>
                    <span className="font-semibold">{selectedPayroll.overtime_amount?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bonuses:</span>
                    <span className="font-semibold">{selectedPayroll.bonuses?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2 col-span-2">
                    <span className="font-bold">Total Earnings:</span>
                    <span className="font-bold text-green-600">{selectedPayroll.total_earnings?.toLocaleString()} SAR</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="bg-red-50 p-4 rounded-xl">
                <h4 className="font-semibold text-red-700 mb-2">Deductions</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span>Absence ({selectedPayroll.absence_days} days):</span>
                    <span className="font-semibold">{selectedPayroll.absence_deduction?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Late Deduction:</span>
                    <span className="font-semibold">{selectedPayroll.late_deduction?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Other Deductions:</span>
                    <span className="font-semibold">{selectedPayroll.other_deductions?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Loan Deduction:</span>
                    <span className="font-semibold">{selectedPayroll.loan_deduction?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GOSI (Employee 9.75%):</span>
                    <span className="font-semibold">{selectedPayroll.gosi_employee?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2 col-span-2">
                    <span className="font-bold">Total Deductions:</span>
                    <span className="font-bold text-red-600">{selectedPayroll.total_deductions?.toLocaleString()} SAR</span>
                  </div>
                </div>
              </div>

              {/* Net Salary */}
              <div className="bg-gradient-to-r from-purple-100 to-indigo-100 p-4 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-purple-700">Net Salary:</span>
                  <span className="text-2xl font-bold text-purple-600">{selectedPayroll.net_salary?.toLocaleString()} SAR</span>
                </div>
              </div>

              {/* Status & Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Period</p>
                  <p className="font-semibold">
                    {new Date(selectedPayroll.period_start).toLocaleDateString()} - {new Date(selectedPayroll.period_end).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <span className={`status-badge ${selectedPayroll.status}`}>
                    {selectedPayroll.status}
                  </span>
                </div>
              </div>

              {selectedPayroll.notes && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Notes</p>
                  <p>{selectedPayroll.notes}</p>
                </div>
              )}
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Payroll Modal */}
      {showEditModal && editingPayroll && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" style={{maxWidth: '900px', width: '95%'}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <Edit className="w-5 h-5 inline mr-2 text-green-600" />
                Edit Payroll - {editingPayroll.payroll_number}
              </h3>
              <button onClick={() => setShowEditModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto space-y-4">
              {/* Employee Info */}
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="font-semibold">{editingPayroll.employee_name}</p>
                <p className="text-sm text-gray-600">{editingPayroll.employee_department} | {editingPayroll.employee_position}</p>
              </div>

              {/* Period */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Period Start</label>
                  <input
                    type="date"
                    value={editingPayroll.period_start}
                    onChange={(e) => handleEditChange('period_start', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Period End</label>
                  <input
                    type="date"
                    value={editingPayroll.period_end}
                    onChange={(e) => handleEditChange('period_end', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Earnings */}
              <div className="bg-green-50 p-4 rounded-xl">
                <h4 className="font-semibold text-green-700 mb-3">Earnings</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="form-group">
                    <label className="form-label text-sm">Base Salary</label>
                    <input
                      type="number"
                      value={editingPayroll.base_salary || 0}
                      onChange={(e) => handleEditChange('base_salary', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Housing Allowance</label>
                    <input
                      type="number"
                      value={editingPayroll.housing_allowance || 0}
                      onChange={(e) => handleEditChange('housing_allowance', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Transport Allowance</label>
                    <input
                      type="number"
                      value={editingPayroll.transport_allowance || 0}
                      onChange={(e) => handleEditChange('transport_allowance', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Food Allowance</label>
                    <input
                      type="number"
                      value={editingPayroll.food_allowance || 0}
                      onChange={(e) => handleEditChange('food_allowance', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Other Allowances</label>
                    <input
                      type="number"
                      value={editingPayroll.other_allowances || 0}
                      onChange={(e) => handleEditChange('other_allowances', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Overtime Amount</label>
                    <input
                      type="number"
                      value={editingPayroll.overtime_amount || 0}
                      onChange={(e) => handleEditChange('overtime_amount', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Bonuses</label>
                    <input
                      type="number"
                      value={editingPayroll.bonuses || 0}
                      onChange={(e) => handleEditChange('bonuses', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group col-span-2">
                    <label className="form-label text-sm">Bonus Reason</label>
                    <input
                      type="text"
                      value={editingPayroll.bonus_reason || ''}
                      onChange={(e) => handleEditChange('bonus_reason', e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="mt-3 p-2 bg-green-100 rounded text-right">
                  <span className="font-bold text-green-700">Total Earnings: {editingPayroll.total_earnings?.toLocaleString()} SAR</span>
                </div>
              </div>

              {/* Deductions */}
              <div className="bg-red-50 p-4 rounded-xl">
                <h4 className="font-semibold text-red-700 mb-3">Deductions</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="form-group">
                    <label className="form-label text-sm">Absence Days</label>
                    <input
                      type="number"
                      value={editingPayroll.absence_days || 0}
                      onChange={(e) => handleEditChange('absence_days', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Absence Deduction</label>
                    <input
                      type="number"
                      value={editingPayroll.absence_deduction || 0}
                      onChange={(e) => handleEditChange('absence_deduction', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Late Deduction</label>
                    <input
                      type="number"
                      value={editingPayroll.late_deduction || 0}
                      onChange={(e) => handleEditChange('late_deduction', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Loan Deduction</label>
                    <input
                      type="number"
                      value={editingPayroll.loan_deduction || 0}
                      onChange={(e) => handleEditChange('loan_deduction', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">GOSI Employee</label>
                    <input
                      type="number"
                      value={editingPayroll.gosi_employee || 0}
                      onChange={(e) => handleEditChange('gosi_employee', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label text-sm">Other Deductions</label>
                    <input
                      type="number"
                      value={editingPayroll.other_deductions || 0}
                      onChange={(e) => handleEditChange('other_deductions', parseFloat(e.target.value))}
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="mt-3 p-2 bg-red-100 rounded text-right">
                  <span className="font-bold text-red-700">Total Deductions: {editingPayroll.total_deductions?.toLocaleString()} SAR</span>
                </div>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  value={editingPayroll.notes || ''}
                  onChange={(e) => handleEditChange('notes', e.target.value)}
                  className="form-input"
                  rows={2}
                />
              </div>

              {/* Net Salary Summary */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 rounded-xl text-white text-center">
                <p className="text-sm opacity-80">Net Salary</p>
                <p className="text-3xl font-bold">{editingPayroll.net_salary?.toLocaleString()} SAR</p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={savePayrollEdit}
                className="btn-primary flex-1"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Loan Modal */}
      {showEditLoanModal && editingLoan && (
        <div className="modal-overlay" onClick={() => setShowEditLoanModal(false)}>
          <div className="modal-content" style={{maxWidth: '700px', width: '95%'}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <Edit className="w-5 h-5 inline mr-2 text-purple-600" />
                Edit Loan - {editingLoan.loan_number}
              </h3>
              <button onClick={() => setShowEditLoanModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto space-y-4">
              {/* Employee Info */}
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="font-semibold">{editingLoan.employee_name}</p>
              </div>

              {/* Loan Type */}
              <div className="form-group">
                <label className="form-label">Loan Type</label>
                <select
                  value={editingLoan.loan_type}
                  onChange={(e) => handleEditLoanChange('loan_type', e.target.value)}
                  className="form-input"
                >
                  <option value="loan">Loan</option>
                  <option value="advance">Advance</option>
                </select>
              </div>

              {/* Amount Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Amount (SAR)</label>
                  <input
                    type="number"
                    value={editingLoan.amount || 0}
                    onChange={(e) => handleEditLoanChange('amount', parseFloat(e.target.value))}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Monthly Deduction (SAR)</label>
                  <input
                    type="number"
                    value={editingLoan.monthly_deduction || 0}
                    onChange={(e) => handleEditLoanChange('monthly_deduction', parseFloat(e.target.value))}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Remaining Balance (SAR)</label>
                  <input
                    type="number"
                    value={editingLoan.remaining_balance || 0}
                    onChange={(e) => handleEditLoanChange('remaining_balance', parseFloat(e.target.value))}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Duration (Months)</label>
                  <input
                    type="number"
                    value={editingLoan.duration_months || 0}
                    onChange={(e) => handleEditLoanChange('duration_months', parseInt(e.target.value))}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    value={editingLoan.start_date || ''}
                    onChange={(e) => handleEditLoanChange('start_date', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    value={editingLoan.end_date || ''}
                    onChange={(e) => handleEditLoanChange('end_date', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Status */}
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  value={editingLoan.status}
                  onChange={(e) => handleEditLoanChange('status', e.target.value)}
                  className="form-input"
                >
                  <option value="active">Active</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Reason */}
              <div className="form-group">
                <label className="form-label">Reason</label>
                <textarea
                  value={editingLoan.reason || ''}
                  onChange={(e) => handleEditLoanChange('reason', e.target.value)}
                  className="form-input"
                  rows={2}
                />
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  value={editingLoan.notes || ''}
                  onChange={(e) => handleEditLoanChange('notes', e.target.value)}
                  className="form-input"
                  rows={2}
                />
              </div>

              {/* Progress Summary */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 rounded-xl text-white">
                <div className="flex justify-between items-center mb-2">
                  <span>Payment Progress</span>
                  <span className="font-bold">
                    {editingLoan.amount ? Math.round((editingLoan.amount - (editingLoan.remaining_balance || 0)) / editingLoan.amount * 100) : 0}%
                  </span>
                </div>
                <div className="w-full bg-white/30 rounded-full h-3">
                  <div 
                    className="bg-white h-3 rounded-full" 
                    style={{width: `${editingLoan.amount ? ((editingLoan.amount - (editingLoan.remaining_balance || 0)) / editingLoan.amount * 100) : 0}%`}}
                  ></div>
                </div>
                <div className="flex justify-between mt-2 text-sm">
                  <span>Paid: {((editingLoan.amount || 0) - (editingLoan.remaining_balance || 0)).toLocaleString()} SAR</span>
                  <span>Remaining: {(editingLoan.remaining_balance || 0).toLocaleString()} SAR</span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowEditLoanModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={saveLoanEdit}
                className="btn-primary flex-1"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payroll;
